import {
  Dermatoscope,
  DermloopClinicalActionTypeEnum,
} from '@dermloop/static-values';
import { Transform } from 'class-transformer';
import {
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsDate,
  IsDateString,
  IsEmail,
  IsEnum,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Max,
  Min,
} from 'class-validator';
import type { JurisdictionalGroupDto } from './jurisdictional-group.dtos';
import type { MedicalUnitGroupDto } from './medical-unit-group.dtos';
import type { ConditionTypeDto } from './condition-type.dtos';
import type { SorDto } from './sor.dtos';

/**
 * DTO representing a medical unit.
 */
export class MedicalUnitDto {
  @IsUUID('all')
  id: string;

  @IsNotEmpty()
  @IsString()
  name: string;

  @IsOptional()
  @IsEmail()
  sharedEmail?: string;

  @IsOptional()
  @IsString()
  address?: string;

  @IsOptional()
  @IsString()
  city?: string;

  @IsOptional()
  @IsString()
  state?: string;

  @IsOptional()
  @IsString()
  zip?: string;

  @IsOptional()
  @IsString()
  fax?: string;

  @IsOptional()
  @IsUUID('all')
  jurisdictionalGroupId?: string;

  @IsOptional()
  jurisdictionalGroup?: JurisdictionalGroupDto;

  @IsOptional()
  medicalUnitGroups?: MedicalUnitGroupDto[];

  @IsOptional()
  @IsBoolean()
  useLesionThickness?: boolean;

  xdis02MessagesEnabled?: boolean;

  xdis91MessagesEnabled?: boolean;

  mitIdMigrationEnabled?: boolean;

  @IsArray()
  @ArrayNotEmpty()
  @IsEnum(DermloopClinicalActionTypeEnum, { each: true })
  defaultAllowedClinicalActions?: DermloopClinicalActionTypeEnum[];

  @IsOptional()
  @IsArray()
  @ArrayNotEmpty()
  conditionTypes?: ConditionTypeDto[];

  @IsOptional()
  @IsArray()
  @ArrayNotEmpty()
  /** @deprecated Use conditionTypes */
  skinConditionTypes?: ConditionTypeDto[];

  preferredTeleSpecialistsId?: string | null;

  allowedTeleSpecialistsId?: string | null;

  sor?: SorDto | null;

  sorId?: string | null;

  sorProviderId?: string | null;

  dermatoscope: Dermatoscope;

  fhirReference?: string;

  nspAuditEnabled?: boolean;

  blockOnNspAuditFailEnabled?: boolean;

  nspAuditBrsCheckDisabled?: boolean;

  seranoteDictationEnabled?: boolean;

  created?: Date;

  description?: string;

  phoneNumber?: string;

  onboardingDate?: Date;

  /**
   * When set and at or before the current time, the unit is closed (no longer
   * operational). A future value schedules the closure. Null/undefined = open.
   */
  closedDate?: Date | null;
}

export class CreateMedicalUnitDto {
  @IsNotEmpty()
  @IsString()
  name: string;

  @IsOptional()
  @IsEmail()
  sharedEmail?: string;

  @IsOptional()
  @IsString()
  address?: string;

  @IsOptional()
  @IsString()
  city?: string;

  @IsOptional()
  @IsString()
  state?: string;

  @IsOptional()
  @IsString()
  zip?: string;

  @IsOptional()
  @IsString()
  fax?: string;

  @IsUUID()
  jurisdictionalGroupId: string;

  // Note that we do not use uuid validation. This caused issues when in an array
  @IsOptional()
  @IsString({ each: true })
  medicalUnitGroupIds?: string[];

  @IsOptional()
  @IsBoolean()
  useLesionThickness?: boolean;

  @IsOptional()
  @IsBoolean()
  xdis02MessagesEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  xdis91MessagesEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  mitIdMigrationEnabled?: boolean;

  @IsArray()
  @ArrayNotEmpty()
  @IsEnum(DermloopClinicalActionTypeEnum, { each: true })
  defaultAllowedClinicalActions: DermloopClinicalActionTypeEnum[];

  // At least one is required, within the unit's federation — enforced in the
  // service since the deprecated alias below may supply it instead.
  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];

  /** @deprecated Use conditionTypeIds */
  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  skinConditionTypeIds?: string[];

  @IsOptional()
  @IsUUID()
  preferredTeleSpecialistsId?: string;

  @IsOptional()
  @IsUUID()
  allowedTeleSpecialistsId?: string;

  @IsOptional()
  @IsString()
  sorId?: string;

  @IsOptional()
  @IsString()
  sorProviderId?: string;

  @IsEnum(Dermatoscope)
  dermatoscope: Dermatoscope;

  @IsOptional()
  @IsString()
  fhirReference?: string;

  @IsOptional()
  @IsBoolean()
  nspAuditEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  blockOnNspAuditFailEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  nspAuditBrsCheckDisabled?: boolean;

  @IsOptional()
  @IsBoolean()
  seranoteDictationEnabled?: boolean;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsString()
  phoneNumber?: string;

  @IsOptional()
  @IsDate()
  onboardingDate?: Date;

  @IsOptional()
  @IsDate()
  closedDate?: Date;
}

export class UpdateMedicalUnitDto {
  @IsOptional()
  @IsNotEmpty()
  @IsString()
  name?: string;

  @IsOptional()
  @IsEmail()
  // Empty string is transformed to null
  @Transform(({ value }) => (value?.length > 0 ? value : null))
  sharedEmail?: string;

  @IsOptional()
  @IsString()
  address?: string;

  @IsOptional()
  @IsString()
  city?: string;

  @IsOptional()
  @IsString()
  state?: string;

  @IsOptional()
  @IsString()
  zip?: string;

  @IsOptional()
  @IsString()
  fax?: string;

  @IsOptional()
  @IsUUID()
  jurisdictionalGroupId?: string;

  // Note that we do not use uuid validation. This caused issues when in an array
  @IsOptional()
  @IsString({ each: true })
  medicalUnitGroupIds?: string[];

  @IsOptional()
  @IsBoolean()
  useLesionThickness?: boolean;

  @IsOptional()
  @IsBoolean()
  xdis02MessagesEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  xdis91MessagesEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  mitIdMigrationEnabled?: boolean;

  @IsArray()
  @ArrayNotEmpty()
  @IsEnum(DermloopClinicalActionTypeEnum, { each: true })
  defaultAllowedClinicalActions: DermloopClinicalActionTypeEnum[];

  // Omitted leaves the assignment alone; an explicit [] is rejected — a unit
  // must always keep at least one condition type.
  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  conditionTypeIds?: string[];

  /** @deprecated Use conditionTypeIds */
  @IsOptional()
  @IsArray()
  @IsUUID('all', { each: true })
  skinConditionTypeIds?: string[];

  /** If set to null, the id will be removed from this entity */
  @IsOptional()
  @IsUUID()
  preferredTeleSpecialistsId?: string | null;

  /** If set to null, the id will be removed from this entity */
  @IsOptional()
  @IsUUID()
  allowedTeleSpecialistsId?: string | null;

  /** If set to null, the SOR ID will be removed from this entity */
  @IsOptional()
  @IsString()
  sorId?: string | null;

  /** If set to null, the provider ID will be removed from this entity */
  @IsOptional()
  @IsString()
  sorProviderId?: string | null;

  @IsOptional()
  @IsEnum(Dermatoscope)
  dermatoscope?: Dermatoscope;

  @IsOptional()
  @IsString()
  fhirReference?: string;

  @IsOptional()
  @IsBoolean()
  nspAuditEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  blockOnNspAuditFailEnabled?: boolean;

  @IsOptional()
  @IsBoolean()
  nspAuditBrsCheckDisabled?: boolean;

  @IsOptional()
  @IsBoolean()
  seranoteDictationEnabled?: boolean;

  @IsOptional()
  @IsString()
  description?: string;

  @IsOptional()
  @IsString()
  phoneNumber?: string;

  @IsOptional()
  @IsDate()
  onboardingDate?: Date;

  /** Set to null to reopen (clear the closure). */
  @IsOptional()
  @IsDate()
  closedDate?: Date | null;
}

/**
 * DTO for querying medical units.
 */
export class MedicalUnitSearchQueryDto {
  @IsOptional()
  @IsNumber()
  @Min(0)
  skip?: number;

  @IsOptional()
  @IsNumber()
  @Min(1)
  @Max(20)
  take?: number;

  /**
   * Search for medical unit name, SOR provider ID, and CVR number. The admin
   * overview endpoint additionally matches jurisdictional group and federation
   * name.
   */
  @IsOptional()
  @IsString()
  searchText?: string;

  @IsOptional()
  @IsUUID('all', { each: true })
  jurisdictionalGroupIds?: string[];

  @IsOptional()
  @IsUUID('all')
  federationId?: string;

  @IsOptional()
  @IsDateString()
  createdOn?: string;

  @IsOptional()
  @IsString()
  sorId?: string;

  /** Include closed units in the results. Defaults to excluding them. */
  @IsOptional()
  @IsBoolean()
  includeClosed?: boolean;
}
